/* eslint-disable @typescript-eslint/no-explicit-any */
import {Edge, EdgeProps, MarkerType, Node} from 'reactflow';

import {createNextState} from '@reduxjs/toolkit';

import {
  EActionType,
  EConditionType,
  EEventType,
  EFlowStepType,
  EGeneratorType,
  ETrigger,
  EUIActionType,
  EUIConditionType,
  EUITrigger,
  TActionStep,
  TAddSubscribersTagAction,
  TBroadcastGenerator,
  TCCDialogSwitchAction,
  TConditionStep,
  TConditionTriggerStep,
  TDeleteSubscribersTagAction,
  TEventOccurredCondition,
  TFlowStep,
  TGeneratorTriggerStep,
  TInMessageButtonPayloadCondition,
  TInMessagePayloadCondition,
  TPauseAction,
  TSendMessageAction,
  TTagQueryCondition,
  TTagQueryWithDelayCondition,
  TTriggerFlowCondition,
  TTriggerPayload,
  TTriggerStep,
  TUIActionPayload,
  TUIAddSubscribersTagAction,
  TUIAnswerTimeoutCondition,
  TUIButtonPressedCondition,
  TUICCDialogSwitchAction,
  TUIConditionPayload,
  TUICreateMessageAction,
  TUIDeleteSubscribersTagAction,
  TUIExternalCondition,
  TUIMessageReceivedCondition,
  TUIPauseAction,
  TUISendMessageAction,
  TUITagAssignTimeoutCondition,
  TUITagPresenceCondition,
  TUITriggerPayload,
  makeTransition,
} from 'src/containers/Triggers/types';
import {uuidv4} from 'src/utils';
import {invariant} from 'src/utils/invariant';

import {ROOT_HANDLE_ID} from './constants';
import {TActionNodeData, makeActionNodeData} from './steps/Action/types';
import {TConditionNodeData, makeConditionNodeData} from './steps/Condition/types';
import {TTriggerNodeData, makeTriggerNodeData} from './steps/Trigger/types';

type TNodeData = TTriggerNodeData | TConditionNodeData | TActionNodeData;
type TNode = Node<TNodeData>;

type TEdgeData = {hasError?: boolean};
type TEdge = Edge<TEdgeData>;
type TEdgeProps = EdgeProps<TEdgeData>;

const makeNode = (
  type: EFlowStepType,
  {
    id,
    data,
    position,
    selected = true,
  }: {data: TNodeData; position: {x: number; y: number}; selected?: boolean; id: string},
): TNode => ({
  id,
  type,
  selected,
  position,
  data,
});

const makeEdge = (
  theme: TTheme['theme'],
  {id, source, target, type = 'floating', sourceHandle}: Omit<TEdge, 'id'> & {id?: TEdge['id']},
  {hasError}: {hasError?: boolean} = {},
): TEdge => ({
  id: id ?? uuidv4(),
  source,
  target,
  type,
  sourceHandle,
  data: {
    hasError,
  },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 16,
    height: 16,
    color: hasError ? theme.palette.red500 : theme.branding.content.primary,
  },
});

type TInjector<T extends TFlowStep> = (step: T, edges: TEdge[]) => T;

const TRANSITION_INJECTORS: {
  [EFlowStepType.TRIGGER]: TInjector<TTriggerStep>;
  [EFlowStepType.CONDITION]: TInjector<TConditionStep>;
  [EFlowStepType.ACTION]: TInjector<TActionStep>;
} = {
  [EFlowStepType.TRIGGER]: (step, edges): TTriggerStep =>
    createNextState(step, (draft) => {
      const targetEdge = edges.find((edge) => edge.source === step.id);

      draft.payload.transition =
        targetEdge !== undefined ? makeTransition(targetEdge.target) : undefined;
    }),
  [EFlowStepType.CONDITION]: (step, edges) =>
    createNextState(step, (draft) => {
      const targetEdge = edges.find(
        (edge) => edge.source === step.id && edge.sourceHandle === ROOT_HANDLE_ID,
      );

      draft.payload.defaultBranch =
        targetEdge !== undefined ? makeTransition(targetEdge.target) : undefined;

      draft.payload.branches.forEach((branch) => {
        const transitionEdge = edges.find((edge) => edge.sourceHandle === branch.condition.id);

        branch.transition =
          transitionEdge !== undefined ? makeTransition(transitionEdge.target) : undefined;
      });
    }),
  [EFlowStepType.ACTION]: (step, edges): TActionStep =>
    createNextState(step, (draft) => {
      const targetEdge = edges.find((edge) => edge.source === step.id);

      draft.payload.transition =
        targetEdge !== undefined ? makeTransition(targetEdge.target) : undefined;
    }) as TActionStep,
};

const UI_TO_BACKEND_CONDITION_MAPPERS: {
  [EUIConditionType.MESSAGE_RECEIVED]: (
    condition: TUIMessageReceivedCondition,
  ) => TInMessagePayloadCondition;
  [EUIConditionType.ANSWER_TIMEOUT_CONDITION]: (
    condition: TUIAnswerTimeoutCondition,
  ) => TEventOccurredCondition;
  [EUIConditionType.BUTTON_PRESSED]: (
    condition: TUIButtonPressedCondition,
  ) => TInMessageButtonPayloadCondition;
  [EUIConditionType.TAG_PRESENCE_CONDITION]: (
    condition: TUITagPresenceCondition,
  ) => TTagQueryCondition;
  [EUIConditionType.TAG_ASSIGN_TIMEOUT_CONDITION]: (
    condition: TUITagAssignTimeoutCondition,
  ) => TTagQueryWithDelayCondition;
  [EUIConditionType.EXTERNAL_TRIGGER]: (condition: TUIExternalCondition) => TTriggerFlowCondition;
} = {
  [EUIConditionType.MESSAGE_RECEIVED]: (condition) => ({
    id: condition.id,
    type: EConditionType.IN_MESSAGE_PAYLOAD_IN_CONDITION,
  }),
  [EUIConditionType.ANSWER_TIMEOUT_CONDITION]: (condition) => ({
    id: condition.id,
    type: EConditionType.EVENT_OCCURRED_CONDITION,
    eventType: EEventType.EXPIRE_ASYNC_CONDITION_COMMAND,
    expirationDuration: condition.expirationDuration,
  }),
  [EUIConditionType.EXTERNAL_TRIGGER]: (condition) => ({
    id: condition.id,
    type: EConditionType.TRIGGER_FLOW_CONDITION,
  }),
  [EUIConditionType.BUTTON_PRESSED]: (condition) => ({
    id: condition.id,
    type: EConditionType.IN_MESSAGE_BUTTON_PAYLOAD_CONDITION,
    button: {
      text: condition.text,
      payload: condition.payload,
      buttonType: condition.buttonType,
    },
  }),
  [EUIConditionType.TAG_ASSIGN_TIMEOUT_CONDITION]: (condition: TUITagAssignTimeoutCondition) => ({
    id: condition.id,
    type: EConditionType.TAG_QUERY_WITH_DELAY_CONDITION,
    tagQueryId: condition.tagQueryId,
  }),
  [EUIConditionType.TAG_PRESENCE_CONDITION]: (condition: TUITagPresenceCondition) => ({
    id: condition.id,
    type: EConditionType.TAG_QUERY_CONDITION,
    tagQueryId: condition.tagQueryId,
  }),
};

const UI_TO_BACKEND_ACTION_MAPPERS = {
  [EUIActionType.CC_DIALOG_SWITCH_ACTION]: (
    action: TUICCDialogSwitchAction,
  ): TCCDialogSwitchAction => ({
    id: action.id,
    type: EActionType.CC_DIALOG_SWITCH_ACTION,
  }),
  [EUIActionType.ADD_SUBSCRIBER_TAG_ACTION]: (
    action: TUIAddSubscribersTagAction,
  ): TAddSubscribersTagAction => ({
    id: action.id,
    type: EActionType.ADD_SUBSCRIBER_TAG_ACTION,

    tagValues: action.tagValues,
  }),
  [EUIActionType.DELETE_SUBSCRIBER_TAG_ACTION]: (
    action: TUIDeleteSubscribersTagAction,
  ): TDeleteSubscribersTagAction => ({
    id: action.id,
    type: EActionType.DELETE_SUBSCRIBER_TAG_ACTION,

    tagValues: action.tagValues,
  }),
  [EUIActionType.PAUSE_ACTION]: (action: TUIPauseAction): TPauseAction => ({
    id: action.id,
    type: EActionType.PAUSE_ACTION,

    delay: action.delay,
    allowedPeriod: action.allowedPeriod,
    timezoneDependent: action.timezoneDependent,
  }),
  [EUIActionType.SEND_MESSAGE_ACTION]: (action: TUISendMessageAction): TSendMessageAction => ({
    id: action.id,
    type: EActionType.SEND_MESSAGE_ACTION,
    messageMatcherId: action.messageMatcherId,
    messageContent: action.messageContent,
    cascade: action.cascade,
  }),
  [EUIActionType.CREATE_MESSAGE_ACTION]: (action: TUICreateMessageAction): TSendMessageAction => ({
    id: action.id,
    type: EActionType.SEND_MESSAGE_ACTION,
    // @ts-expect-error: ok
    messageContent: action.messageContent,
  }),
};

const UI_TO_BACKEND_TRIGGER_MAPPERS = {
  [EUIConditionType.EMPTY]: () => {
    throw new Error('Condition is empty');
  },
  [EUIConditionType.TAG_ASSIGN_TIMEOUT_CONDITION]: (
    step: TTriggerStep<TUITriggerPayload<TUITagPresenceCondition>>,
  ): TTriggerStep<TTriggerPayload<TGeneratorTriggerStep<TBroadcastGenerator>>> => {
    return {
      ...step,
      payload: {
        ...step.payload,
        type: ETrigger.GENERATOR_TRIGGER_STEP,
        eventType: EEventType.START_GENERATOR_TRIGGER_FLOW_EVENT,
        switchDialogToBot: true,
        generator: {
          id: step.payload.condition.id,
          type: EGeneratorType.BROADCAST_GENERATOR_WITH_DELAY,
          tagQueryId: step.payload.condition.tagQueryId,
        },
      },
    };
  },
  [EUIConditionType.TAG_PRESENCE_CONDITION]: (
    step: TTriggerStep<TUITriggerPayload<TUITagPresenceCondition>>,
  ): TTriggerStep<TTriggerPayload<TGeneratorTriggerStep<TBroadcastGenerator>>> => {
    return {
      ...step,
      payload: {
        ...step.payload,
        type: ETrigger.GENERATOR_TRIGGER_STEP,
        eventType: EEventType.START_GENERATOR_TRIGGER_FLOW_EVENT,
        switchDialogToBot: true,
        generator: {
          id: step.payload.condition.id,
          type: EGeneratorType.BROADCAST_GENERATOR,
          tagQueryId: step.payload.condition.tagQueryId,
        },
      },
    };
  },
  [EUIConditionType.MESSAGE_RECEIVED]: (
    step: TTriggerStep<TUITriggerPayload<TUIMessageReceivedCondition>>,
  ): TTriggerStep<TTriggerPayload<TConditionTriggerStep<TInMessagePayloadCondition>>> => {
    return {
      ...step,
      payload: {
        ...step.payload,
        type: ETrigger.CONDITION_TRIGGER_STEP,
        switchDialogToBot: true,
        eventType: EEventType.IN_MESSAGE_FLOW_EVENT,
        condition: {
          id: step.payload.condition.id,
          type: EConditionType.IN_MESSAGE_PAYLOAD_IN_CONDITION,
        },
      },
    };
  },
  [EUIConditionType.EXTERNAL_TRIGGER]: (
    step: TTriggerStep<TUITriggerPayload<TUIExternalCondition>>,
  ): TTriggerStep<TTriggerPayload<TConditionTriggerStep<TTriggerFlowCondition>>> => {
    return {
      ...step,
      payload: {
        ...step.payload,
        type: ETrigger.CONDITION_TRIGGER_STEP,
        switchDialogToBot: false,
        eventType: EEventType.TRIGGER_FLOW_EVENT,
        condition: {
          id: step.payload.condition.id,
          type: EConditionType.TRIGGER_FLOW_CONDITION,
        },
      },
    };
  },
};

const UI_TO_BACKEND_STEP_MAPPERS = {
  [EFlowStepType.ACTION]: (step: TActionStep<TUIActionPayload>): TActionStep => ({
    ...step,
    payload: {
      ...step.payload,
      action: step.payload.action
        ? UI_TO_BACKEND_ACTION_MAPPERS[step.payload.action.type](step.payload.action as any)
        : undefined,
    },
  }),
  [EFlowStepType.CONDITION]: (step: TConditionStep<TUIConditionPayload>): TConditionStep => ({
    ...step,
    payload: {
      ...step.payload,
      branches: step.payload.branches.map((branch) => ({
        ...branch,
        condition: UI_TO_BACKEND_CONDITION_MAPPERS[branch.condition.type](branch.condition as any),
      })),
    },
  }),
  [EFlowStepType.TRIGGER]: (step: TTriggerStep<TUITriggerPayload>): TTriggerStep<TTriggerPayload> =>
    UI_TO_BACKEND_TRIGGER_MAPPERS[step.payload.condition.type](step as any),
};

const BACKEND_TO_UI_TRIGGER_MAPPERS = {
  [ETrigger.CONDITION_TRIGGER_STEP]: (
    step: TTriggerStep<TTriggerPayload<TConditionTriggerStep>>,
  ) => {
    switch (step.payload.condition.type) {
      case EConditionType.IN_MESSAGE_PAYLOAD_IN_CONDITION:
        return {
          ...step,
          payload: {
            ...step.payload,
            type: EUITrigger.EMPTY_TRIGGER_STEP,
            condition: {
              id: step.payload.condition.id,
              type: EUIConditionType.MESSAGE_RECEIVED,
              disabled: false,
            },
          },
        } as TTriggerStep<TUITriggerPayload<TUIMessageReceivedCondition>>;
      case EConditionType.TRIGGER_FLOW_CONDITION:
        return {
          ...step,
          payload: {
            ...step.payload,
            type: EUITrigger.EMPTY_TRIGGER_STEP,
            condition: {
              id: step.payload.condition.id,
              type: EUIConditionType.EXTERNAL_TRIGGER,
              disabled: false,
            },
          },
        } as TTriggerStep<TUITriggerPayload<TUIExternalCondition>>;
    }
  },
  [ETrigger.GENERATOR_TRIGGER_STEP]: (
    step: TTriggerStep<TTriggerPayload<TGeneratorTriggerStep>>,
  ) => {
    switch (step.payload.generator.type) {
      case EGeneratorType.BROADCAST_GENERATOR:
        return {
          ...step,
          payload: {
            type: EUITrigger.EMPTY_TRIGGER_STEP,
            condition: {
              id: step.payload.generator.id,
              type: EUIConditionType.TAG_PRESENCE_CONDITION,
              tagQueryId: step.payload.generator.tagQueryId,
              disabled: false,
            },
          },
        } as TTriggerStep<TUITriggerPayload<TUITagPresenceCondition>>;
      case EGeneratorType.BROADCAST_GENERATOR_WITH_DELAY:
        return {
          ...step,
          payload: {
            type: EUITrigger.EMPTY_TRIGGER_STEP,
            condition: {
              id: step.payload.generator.id,
              type: EUIConditionType.TAG_ASSIGN_TIMEOUT_CONDITION,
              tagQueryId: step.payload.generator.tagQueryId,
              disabled: false,
            },
          },
        } as TTriggerStep<TUITriggerPayload<TUITagAssignTimeoutCondition>>;
    }
  },
};

const BACKEND_TO_UI_CONDITION_MAPPERS = {
  [EConditionType.EVENT_OCCURRED_CONDITION]: (
    condition: TEventOccurredCondition,
  ): TUIAnswerTimeoutCondition => ({
    id: condition.id,
    type: EUIConditionType.ANSWER_TIMEOUT_CONDITION,
    expirationDuration: condition.expirationDuration,
    disabled: false,
  }),
  [EConditionType.IN_MESSAGE_BUTTON_PAYLOAD_CONDITION]: (
    condition: TInMessageButtonPayloadCondition,
  ): TUIButtonPressedCondition => ({
    id: condition.id,
    type: EUIConditionType.BUTTON_PRESSED,
    payload: condition.button.payload,
    buttonType: condition.button.buttonType,
    text: condition.button.text,
    disabled: false,
  }),
  [EConditionType.IN_MESSAGE_PAYLOAD_IN_CONDITION]: (
    condition: TInMessagePayloadCondition,
  ): TUIMessageReceivedCondition => ({
    id: condition.id,
    type: EUIConditionType.MESSAGE_RECEIVED,
    disabled: false,
  }),
  [EConditionType.TRIGGER_FLOW_CONDITION]: (
    condition: TTriggerFlowCondition,
  ): TUIExternalCondition => ({
    id: condition.id,
    type: EUIConditionType.EXTERNAL_TRIGGER,
    disabled: false,
  }),
  [EConditionType.TAG_QUERY_WITH_DELAY_CONDITION]: (
    condition: TTagQueryWithDelayCondition,
  ): TUITagAssignTimeoutCondition => ({
    id: condition.id,
    type: EUIConditionType.TAG_ASSIGN_TIMEOUT_CONDITION,
    tagQueryId: condition.tagQueryId,
    disabled: false,
  }),
  [EConditionType.TAG_QUERY_CONDITION]: (
    condition: TTagQueryCondition,
  ): TUITagPresenceCondition => ({
    id: condition.id,
    type: EUIConditionType.TAG_PRESENCE_CONDITION,
    tagQueryId: condition.tagQueryId,
    disabled: false,
  }),
};

const BACKEND_TO_UI_ACTION_MAPPERS = {
  [EActionType.CC_DIALOG_SWITCH_ACTION]: (
    action: TCCDialogSwitchAction,
  ): TUICCDialogSwitchAction => ({
    id: action.id,
    disabled: false,
    type: EUIActionType.CC_DIALOG_SWITCH_ACTION,
  }),
  [EActionType.ADD_SUBSCRIBER_TAG_ACTION]: (
    action: TAddSubscribersTagAction,
  ): TUIAddSubscribersTagAction => ({
    id: action.id,
    disabled: false,
    type: EUIActionType.ADD_SUBSCRIBER_TAG_ACTION,

    tagValues: action.tagValues,
  }),
  [EActionType.DELETE_SUBSCRIBER_TAG_ACTION]: (
    action: TDeleteSubscribersTagAction,
  ): TUIDeleteSubscribersTagAction => ({
    id: action.id,
    disabled: false,
    type: EUIActionType.DELETE_SUBSCRIBER_TAG_ACTION,

    tagValues: action.tagValues,
  }),
  [EActionType.PAUSE_ACTION]: (action: TPauseAction): TUIPauseAction => ({
    id: action.id,
    disabled: false,
    type: EUIActionType.PAUSE_ACTION,

    delay: action.delay,
    allowedPeriod: action.allowedPeriod,
    timezoneDependent: action.timezoneDependent,
  }),
  [EActionType.SEND_MESSAGE_ACTION]: (
    action: TSendMessageAction,
  ): TUISendMessageAction | TUICreateMessageAction => {
    if (action.messageMatcherId) {
      const data: TUISendMessageAction = {
        id: action.id,
        disabled: false,
        type: EUIActionType.SEND_MESSAGE_ACTION,
        messageMatcherId: action.messageMatcherId,
        messageContent: action.messageContent,
        cascade: action.cascade,
      };

      return data;
    }

    const data: TUICreateMessageAction = {
      id: action.id,
      disabled: false,
      type: EUIActionType.CREATE_MESSAGE_ACTION,
      messageContent: action.messageContent as TUICreateMessageAction['messageContent'],
    };

    return data;
  },
};

const makeDepthsMap = (startStepId: string, edges: TEdge[]) => {
  const visitedNodes: Record<string, boolean> = {};

  const helper = (stepId: string, depth: number): Record<string, number> => {
    visitedNodes[stepId] = true;

    return edges
      .filter((edge) => edge.source === stepId)
      .map((edge) => edge.target)
      .filter((nextStepId) => !visitedNodes[nextStepId])
      .reduce(
        (acc, nextStepId) => ({
          ...acc,
          ...helper(nextStepId, depth + 1),
        }),
        {[stepId]: depth},
      );
  };

  return helper(startStepId, 0);
};

type TExtractor<T extends TFlowStep> = (step: T, theme: TTheme['theme']) => TEdge[];

const EDGE_EXTRACTORS: {
  [EFlowStepType.TRIGGER]: TExtractor<TTriggerStep>;
  [EFlowStepType.CONDITION]: TExtractor<TConditionStep>;
  [EFlowStepType.ACTION]: TExtractor<TActionStep>;
} = {
  [EFlowStepType.TRIGGER]: (step, theme) => {
    if (step.payload.transition === undefined) {
      return [];
    }

    const {transition} = step.payload;

    return [
      makeEdge(theme, {
        id: transition.id,
        source: step.id,
        target: transition.toStepId,
        sourceHandle: ROOT_HANDLE_ID,
      }),
    ];
  },
  [EFlowStepType.CONDITION]: (step, theme) => {
    const edges = [];

    if (step.payload.defaultBranch !== undefined) {
      edges.push(
        makeEdge(theme, {
          id: step.payload.defaultBranch.id,
          source: step.id,
          target: step.payload.defaultBranch.toStepId,
          sourceHandle: ROOT_HANDLE_ID,
        }),
      );
    }

    return step.payload.branches
      .filter((branch) => branch.transition !== undefined)
      .map((branch) =>
        makeEdge(theme, {
          id: branch.transition!.id,
          source: step.id,
          target: branch.transition!.toStepId,
          sourceHandle: branch.condition.id,
        }),
      )
      .concat(edges);
  },
  [EFlowStepType.ACTION]: (step, theme) => {
    if (step.payload.transition === undefined) {
      return [];
    }

    const {transition} = step.payload;

    return [
      makeEdge(theme, {
        id: transition.id,
        source: step.id,
        target: transition.toStepId,
        sourceHandle: ROOT_HANDLE_ID,
      }),
    ];
  },
};

export const getDepthsMap = ({nodes, edges}: {nodes: TNode[]; edges: TEdge[]}) => {
  const triggerStep = nodes.find((node) => node.type === EFlowStepType.TRIGGER);

  invariant('Flow should contain trigger step', triggerStep);

  return makeDepthsMap(triggerStep.id, edges);
};

const mapNodesToSteps = (flowId: string, {nodes, edges}: {nodes: TNode[]; edges: TEdge[]}) => {
  const depthsMap = getDepthsMap({nodes, edges});
  const steps = nodes.map((node) => ({...node.data.step, meta: {position: node.position}}));

  return (
    steps
      // @ts-expect-error: TODO: research how to bind EFlowStepType to exact TFlowStep
      .map((step) => TRANSITION_INJECTORS[step.type](step, edges))
      .map((step) => ({...step, depth: depthsMap[step.id]}))
      .map((step) => ({...step, flowId}))
      // @ts-expect-error: TODO: research how to bind EFlowStepType to exact TFlowStep
      .map((step) => UI_TO_BACKEND_STEP_MAPPERS[step.type](step))
  );
};

const NODE_DATA_FACTORIES = {
  [EFlowStepType.TRIGGER]: (step: TTriggerStep) => {
    return makeTriggerNodeData(
      BACKEND_TO_UI_TRIGGER_MAPPERS[step.payload.type](step as any) as any,
    );
  },
  [EFlowStepType.CONDITION]: (step: TConditionStep) => {
    const hasButtonPressedCondition = step.payload.branches.some(
      (branch) => branch.condition.type === EConditionType.IN_MESSAGE_BUTTON_PAYLOAD_CONDITION,
    );

    return makeConditionNodeData({
      ...step,
      payload: {
        ...step.payload,
        branches: step.payload.branches
          .filter(
            (branch) =>
              hasButtonPressedCondition ||
              branch.condition.type !== EConditionType.EVENT_OCCURRED_CONDITION,
          )
          .map((branch) => ({
            ...branch,
            condition: BACKEND_TO_UI_CONDITION_MAPPERS[branch.condition.type](
              branch.condition as any,
            ),
          })),
      },
    });
  },
  [EFlowStepType.ACTION]: (step: TActionStep) =>
    makeActionNodeData({
      ...step,
      payload: {
        ...step.payload,
        action: step.payload.action
          ? BACKEND_TO_UI_ACTION_MAPPERS[step.payload.action.type](step.payload.action as any)
          : undefined,
      },
    }),
};

const mapStepsToNodes = (steps: TFlowStep[]) => {
  return steps.map((step) =>
    makeNode(step.type, {
      selected: false,
      position: {x: step.meta.position.x, y: step.meta.position.y},
      id: step.id,
      // @ts-expect-error: TODO: research how to bind EFlowStepType to exact TFlowStep
      data: NODE_DATA_FACTORIES[step.type](step),
    }),
  );
};

const mapStepsToEdges = (theme: TTheme['theme'], steps: TFlowStep[]) =>
  steps.flatMap((step) => EDGE_EXTRACTORS[step.type](step as any, theme));

export {
  TNode,
  makeNode,
  makeEdge,
  TNodeData,
  mapNodesToSteps,
  mapStepsToNodes,
  mapStepsToEdges,
  TEdge,
  TEdgeProps,
  TEdgeData,
};
/* eslint-enable @typescript-eslint/no-explicit-any */
